import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Button, Dialog, DialogActions, DialogContent, Grid, TextField, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EmailInput from '../common/emailinput';
import ParameterSelector from '../common/parameterselector';
import { normalizePhonenumber, verifyPhonenumber } from '../common/common';
import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import DeliveryDatePicker from './deliveryDatePicker';
import OrderTypeSelector from './ordertypeselector';
import Order from '../domain/order';
import { useTranslation } from 'react-i18next';
import DeliveryDateDetails from './deliverydatedetails';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
var isSameOrBefore = require('dayjs/plugin/isSameOrBefore');
var isSameOrAfter = require('dayjs/plugin/isSameOrAfter');
dayjs.extend(utc);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export default function OrderMassEditDialog(props) {
    const [formData, setFormData] = useState({
        orderType: 'Normal',
        deliveryTime: null,
        earlyDeliveryTime: null,
    });
    const [earliestDeliveryDate, setEarliestDeliveryDate] = useState(null);
    const [earliestDeliveryDateDetails, setEarliestDeliveryDateDetails] = useState(null);
    const [phoneValue, setPhoneValue] = useState(props.defaultValues.ordererTel || '');
    const [phoneEdited, setPhoneEdited] = useState(false);
    const customerView = props.customerView;
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const prevFormDataRef = useRef();
    const prevFormData = prevFormDataRef.current;
    const getEarliestDeliveryTimeProps = props.getEarliestDeliveryTime;
    const { t } = useTranslation();

    const getEarliestDeliveryTime = useCallback(async () => {
        if (props.open) {
            const earliestDeliveryDateInfo = await getEarliestDeliveryTimeProps(formData['orderType']);
            setEarliestDeliveryDate(earliestDeliveryDateInfo.earliestDate);
            setEarliestDeliveryDateDetails(earliestDeliveryDateInfo.earliestDateDetails);
        }
    }, [formData, getEarliestDeliveryTimeProps, props.open]);

    useEffect(() => {
        if (props.open) {
            const tel = props.defaultValues.ordererTel || '';
            const telIsInvalid = tel && !verifyPhonenumber(tel);
            setPhoneValue(telIsInvalid ? '' : tel);
            setPhoneEdited(false);
            if (telIsInvalid) {
                // Clear the invalid phone number so it doesn't get silently applied to all selected orders.
                setFormData((prev) => {
                    const data = cloneDeep(prev);
                    data['ordererTel'] = '';
                    return data;
                });
            }
        }
    }, [props.open, props.defaultValues.ordererTel]);

    useEffect(() => {
        prevFormDataRef.current = formData;
    }, [formData]);

    useEffect(() => {
        if (prevFormData && prevFormData['orderType'] !== formData['orderType']) {
            const data = cloneDeep(formData);
            data['deliveryTime'] = null;
            data['earlyDeliveryTime'] = null;
            setFormData(data);
            getEarliestDeliveryTime();
        } else if (props.open && !earliestDeliveryDate) {
            getEarliestDeliveryTime();
        }
    }, [formData, getEarliestDeliveryTime, prevFormData, earliestDeliveryDate, props.open]);

    const onSave = async () => {
        const dataToSend = {
            ...formData,
        };
        await props.save(dataToSend);
    };

    const onCancel = () => {
        setFormData({});
        props.cancel();
    };

    const onChange = (e) => {
        const data = cloneDeep(formData);
        if (e.target.name === 'deliveryMethod') {
            data['deliveryTime'] = null;
            data['earlyDeliveryTime'] = null;
            const param = props.parameters['DeliveryMethod'].find((p) => p.id === e.target.value);
            data[e.target.name] = param.value;
        } else {
            data[e.target.name] = e.target.value;
        }
        setFormData(data);
    };

    const onEmailChange = (emails) => {
        let data = formData;
        if (emails.length === 0) {
            delete data['confirmationEmail'];
        } else {
            data['confirmationEmail'] = emails;
        }
        setFormData(data);
    };

    const onDeliveryEmailChange = (emails) => {
        let data = formData;
        if (emails.length === 0) {
            delete data['deliveryConfirmationEmail'];
        } else {
            data['deliveryConfirmationEmail'] = emails;
        }
        setFormData(data);
    };

    const onDateChange = async (date) => {
        let earlyDate = null;
        let earlyDateFromUi = dayjs(formData['earlyDeliveryTime']);
        let selectedDate = dayjs(date);

        if (selectedDate.isValid()) {
            selectedDate = selectedDate.hour(12).minute(0).second(0);
        } else {
            selectedDate = null;
        }

        if (props.customerView) {
            earlyDate = selectedDate;
        } else if (selectedDate === null) {
            earlyDate = null;
        } else if (earlyDateFromUi.isValid() && earlyDateFromUi.isAfter(selectedDate, 'day')) {
            earlyDate = selectedDate;
        } else {
            earlyDate = earlyDateFromUi.isValid() ? earlyDateFromUi : null;
        }

        const data = cloneDeep(formData);

        let deliveryTime = selectedDate ? selectedDate : null;
        if (deliveryTime && deliveryTime instanceof dayjs) {
            deliveryTime = deliveryTime.utc().format();
        }
        let earlyDeliveryTime = earlyDate ? earlyDate : null;
        if (earlyDeliveryTime && earlyDeliveryTime instanceof dayjs) {
            earlyDeliveryTime = earlyDeliveryTime.utc().format();
        }
        data['deliveryTime'] = deliveryTime;
        data['earlyDeliveryTime'] = earlyDeliveryTime;
        setFormData(data);
    };

    const onEarlyDateChange = (date) => {
        let selectedDate = dayjs(date);

        if (selectedDate.isValid()) {
            selectedDate = selectedDate.hour(12).minute(0).second(0);
            if (selectedDate.isAfter(dayjs(formData['deliveryTime'], 'day'))) {
                toast.error(t('order.earliestDeliveryDateCannotByLaterThanDeliveryDate'), { autoClose: 5000 });
                selectedDate = null;
            }
        } else {
            selectedDate = null;
        }

        let earlyDeliveryTime = selectedDate ? selectedDate : null;
        if (earlyDeliveryTime && earlyDeliveryTime instanceof dayjs) {
            earlyDeliveryTime = earlyDeliveryTime.utc().format();
        }
        const data = cloneDeep(formData);
        data['earlyDeliveryTime'] = earlyDeliveryTime;
        setFormData(data);
    };

    const isSaveDisabled = () => {
        if (formData['orderType'] === Order.OrderTypeUrgent()) return false;
        return formData['deliveryTime'] != null && formData['earlyDeliveryTime'] == null;
    };

    const getParametersObject = (type, text) => {
        return props.parameters[type].find((p) => p.value === text);
    };

    return (
        <Dialog
            aria-labelledby="mass-edit-dialog"
            open={props.open}
            fullWidth={true}
            maxWidth={'md'}
            fullScreen={fullScreen}>
            <DialogContent dividers>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        {t('order.changesWillBeCopiedToSelectedOrders')}
                    </Grid>
                    {!customerView && (
                        <Grid item xs={12} sm={6}>
                            <OrderTypeSelector
                                handleChange={(value) => onChange({ target: { value, name: 'orderType' } })}
                                selected={formData['orderType']}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                        <DeliveryDateDetails deliveryDateDetails={earliestDeliveryDateDetails} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DeliveryDatePicker
                            label={t('general.deliveryDate') + ' *'}
                            value={formData['deliveryTime']}
                            onChange={onDateChange}
                            id={'mass-edit-delivery-time'}
                            name={'mass-edit-delivery-time'}
                            earliestDeliveryDate={earliestDeliveryDate}
                            //onOpen={onOpenDeliveryDatePicker}
                            disabled={!earliestDeliveryDate}
                            deliveryMethod={formData['deliveryMethod']}
                        />
                    </Grid>

                    {!customerView && formData['orderType'] !== Order.OrderTypeUrgent() && (
                        <Grid item xs={12} md={6}>
                            <DeliveryDatePicker
                                label={t('order.earliestDeliveryDate') + ' *'}
                                value={formData['earlyDeliveryTime']}
                                onChange={onEarlyDateChange}
                                id={'mass-edit-early-delivery-time'}
                                name={'mass-edit-early-delivery-time'}
                                latestDeliveryDate={formData['deliveryTime']}
                                disabled={!dayjs(formData['deliveryTime']).isValid()}
                                enableAllWeekDays={true}
                                //onOpen={onOpenDeliveryDatePicker}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                        <ParameterSelector
                            values={props.parameters}
                            category={'DeliveryMethod'}
                            selected={getParametersObject('DeliveryMethod', formData['deliveryMethod'])}
                            handleChange={(type, value) => onChange({ target: { value, name: 'deliveryMethod' } })}
                            required={true}
                            blockedIds={props.blockedDeliveryMethodIds}
                            blockedMessage={t('cargo.deliveryMethodBlockedReason')}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            required
                            id="deliveryTerms"
                            label={t('parameterCategories.DeliveryTerms')}
                            sx={{ width: '100%' }}
                            onChange={onChange}
                            margin="normal"
                            name="deliveryTerms"
                            defaultValue={props.defaultValues.deliveryTerms}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            required
                            id="ordererName"
                            label={t('order.ordererName')}
                            sx={{ width: '100%' }}
                            onChange={onChange}
                            margin="normal"
                            name="ordererName"
                            defaultValue={props.defaultValues.ordererName}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            required
                            id="ordererTel"
                            label={t('order.deliveryRecipientTel')}
                            sx={{ width: '100%' }}
                            value={phoneValue}
                            onChange={(e) => {
                                setPhoneEdited(true);
                                setPhoneValue(e.target.value);
                                const data = cloneDeep(formData);
                                data['ordererTel'] = e.target.value;
                                setFormData(data);
                            }}
                            onBlur={(e) => {
                                const normalized = normalizePhonenumber(e.target.value);
                                const finalValue = normalized || e.target.value;
                                setPhoneValue(finalValue);
                                if (phoneEdited) {
                                    const data = cloneDeep(formData);
                                    data['ordererTel'] = finalValue;
                                    setFormData(data);
                                }
                            }}
                            margin="normal"
                            name="ordererTel"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <EmailInput
                            required={true}
                            label={t('order.confirmationEmail')}
                            name="confirmationEmail"
                            domElemName="confirmationEmail"
                            onChange={onEmailChange}
                            emails={
                                formData['confirmationEmail']
                                    ? formData['confirmationEmail']
                                    : props.defaultValues.confirmationEmail
                            }
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <EmailInput
                            label={t('order.deliveryConfirmationMail')}
                            name="massedit-delivery-confirmation-email"
                            domElemName="massedit-delivery-confirmation-email"
                            onChange={onDeliveryEmailChange}
                            emails={
                                formData['deliveryConfirmationEmail']
                                    ? formData['deliveryConfirmationEmail']
                                    : props.defaultValues.deliveryConfirmationEmail
                            }
                            fullWidth={true}
                            hint={t('order.deliveryConfirmationHint')}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            required
                            id="reference"
                            label={t('general.yourReference')}
                            sx={{ width: '100%' }}
                            onChange={onChange}
                            margin="normal"
                            name="reference"
                            defaultValue={props.defaultValues.reference}
                        />
                    </Grid>
                    {!customerView && (
                        <Grid item xs={12} md={6}>
                            <TextField
                                multiline={true}
                                id="packingInstructions"
                                name="packingInstructions"
                                label={t('general.packingInstructions')}
                                sx={{ width: '100%' }}
                                margin="normal"
                                onChange={onChange}
                            />
                        </Grid>
                    )}
                    {!customerView && (
                        <Grid item xs={12} md={6}>
                            <TextField
                                multiline={true}
                                id="generalInfo"
                                name="generalInfo"
                                label={t('order.generalInfoToProduction')}
                                sx={{ width: '100%' }}
                                margin="normal"
                                onChange={onChange}
                            />
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="inherit" name="mass-edit-cancel" onClick={onCancel}>
                    {t('buttons.cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    name="mass-edit-save"
                    onClick={onSave}
                    disabled={isSaveDisabled()}>
                    {t('buttons.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

OrderMassEditDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    cancel: PropTypes.func.isRequired,
    save: PropTypes.func.isRequired,
    customerView: PropTypes.bool,
    parameters: PropTypes.object.isRequired,
    getEarliestDeliveryTime: PropTypes.func.isRequired,
    blockedDeliveryMethodIds: PropTypes.array,
};
